import React, { useState, useEffect, useRef } from 'react';
import { TbPlug } from 'react-icons/tb';
// import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../../features/theme/context/ThemeContext';

import ConnectToolsModal from '../../../components/layout/ConnectToolsModal';
import './ConnectYourToolsBar.css';
import gmailSvg from '../../../assets/gmail.svg';
import googleCalendarSvg from '../../../assets/google-calendar.svg';
import driveSvg from '../../../assets/drive.svg';
import outlookSvg from '../../../assets/outlook.svg';
import sharepointSvg from '../../../assets/sharepoint.svg';

// Local SVGs for multi-colored logos; CDN for single-color icons
const TOOLS = [
  { src: outlookSvg, label: 'Outlook' },
  { src: gmailSvg, label: 'Gmail' },
  { src: googleCalendarSvg, label: 'Google Calendar' },
  { src: driveSvg, label: 'Google Drive' },
  { src: sharepointSvg, label: 'SharePoint' },
  { slug: 'hubspot', label: 'HubSpot', color: 'FF7A59', darkColor: 'FF7A59' },
  { slug: 'github', label: 'GitHub', color: '181717', darkColor: 'F0F0F0' },
  { slug: 'notion', label: 'Notion', color: '000000', darkColor: 'F0F0F0' },
];

const COMPACT_BREAKPOINT = 450;

const ConnectYourToolsBar = ({ appearsConnected = false, borderRadius = 20 }) => {
  const { darkMode } = useTheme();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect ?? {};
      if (typeof width === 'number') setIsCompact(width < COMPACT_BREAKPOINT);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const closeConnectModal = () => {
    setShowConnectModal(false);
    try {
      localStorage.removeItem('connectToolsModalOpen');
      const params = new URLSearchParams(window.location.search);
      params.delete('connectTools');
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname
      );
    } catch {}
  };

  const handleClick = () => {
    setShowConnectModal(true);
    try {
      localStorage.setItem('connectToolsModalOpen', '1');
      const params = new URLSearchParams(window.location.search);
      params.set('connectTools', '1');
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    } catch {}
  };

  return (
    <>
      <div
        ref={containerRef}
        onClick={handleClick}
        className={`flex items-center justify-between transition-colors cursor-pointer group ${
          appearsConnected
            ? 'connect-tools-bar connect-tools-bar-rounded-b border-x border-b border-gray-200 dark:border-[#282828] rounded-b-[42px] pb-[7px] px-5 pt-[29px] -mt-[22px] mx-3 z-10'
            : 'connect-tools-bar-squircle mt-2 rounded-2xl border border-gray-300 dark:border-[#282828] px-4 py-2.5 pt-3 bg-[#ebebeb] dark:bg-[#141414] hover:bg-[#e8e8e8] dark:hover:bg-[#171717]'
        }`}
      >
        <div className="flex items-center gap-2">
          <TbPlug className="w-[18px] h-[18px] text-gray-500 dark:text-[#999999]" />
          <span className="text-[13px] text-gray-600 dark:text-[#999999]">
            {isCompact ? 'Connect your tools' : 'Connect your tools to Midus'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-1.5">
            {(isCompact ? TOOLS.slice(0, 4) : TOOLS).map((tool, i) => {
              const imgSrc = tool.src
                ? tool.src
                : `https://cdn.simpleicons.org/${tool.slug}/${darkMode && tool.darkColor ? tool.darkColor : tool.color}`;
              return (
                <div
                  key={i}
                  className={`w-6 h-6 rounded-full flex items-center justify-center border border-gray-200 dark:border-[#333] flex-shrink-0 relative p-1 ${appearsConnected ? 'connect-tool-icon' : 'bg-[#ebebeb] dark:bg-[#141414]'}`}
                  style={{ zIndex: (isCompact ? 4 : TOOLS.length) - i }}
                  title={tool.label}
                >
                  <img
                    src={imgSrc}
                    alt={tool.label}
                    className="w-full h-full object-contain"
                  />
                </div>
              );
            })}
          </div>
          {/*
          <button
            type="button"
            onClick={handleDismiss}
            className="connect-tools-bar-squircle-sm p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-[#999999] dark:hover:bg-[#282828] transition-colors opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
          */}
        </div>
      </div>
      <ConnectToolsModal isOpen={showConnectModal} onClose={closeConnectModal} darkMode={darkMode} />
    </>
  );
};

export default ConnectYourToolsBar;
